import type { MouseEvent } from 'react';
import { ChatIcon, CopySimpleIcon, InfoIcon } from '@phosphor-icons/react';
import type {
  OrderError,
  OrderPartialCancellation,
  OrderProviderSend,
  OrderStage,
  OrderStatusReason,
} from '@skupreme/api-contracts/core';
import {
  Button,
  Checkbox,
  CopyButton,
  Popover,
  Tooltip,
  buttonStyles,
  copyToClipboard,
} from '@skupreme/shared/ui';
import { tooltipTexts } from '@utility/constants/common';
import type { MarketplaceIconData } from '@utility/marketplace';
import { getOrderIssues, type OrderIssue } from '../../lib/issues';
import { StopRowClick } from '../common/StopRowClick';
import {
  getFulfillmentOrderId,
  getPublicLabelLink,
  withOrderPrefix,
  type FulfillmentRecordItem,
} from '../../lib/order-id';
import { channelLabel } from '../../lib/channel';
import { MarketplaceAvatar } from '../common/MarketplaceAvatar';
import { OrderIssues } from '../common/OrderIssues';
import { PartiallyCanceledBadge } from '../common/PartiallyCanceledBadge';
import { getPastSla } from '../../lib/past-sla';
import { PastSlaBadge } from '../common/PastSlaBadge';
import { getStatusReason } from '../../lib/status-reason';
import { StatusReasonBadge } from '../common/StatusReasonBadge';
import { HeldBadge } from '../common/HeldBadge';
import { StageBadge } from '../common/StageBadge';
import {
  getProviderSend,
  withoutWaitingForSend,
} from '../../lib/provider-send';
import { ProviderSendBadge } from '../common/ProviderSendBadge';

export type OrderIdCellOrder = {
  marketplace_order_display_id?: string;
  marketplace?: (MarketplaceIconData & { region_name?: string }) | null;
  memo?: string;
  /** The storefront's own name, when the seller has more than one. */
  shopName?: string;
  order_error?: OrderError[];
  /** Only when a line was removed while another was not. */
  partially_canceled?: OrderPartialCancellation;
  /** V2 only; read with `getPastSla`, which drops a malformed value. */
  past_sla?: unknown;
  /** V2 only (EN-4403): why the order is held or waits at its provider. */
  status_reason?: OrderStatusReason | null;
  /** V2 only: where the order is in its life, finer than the tab. */
  stage?: OrderStage | null;
  /** V2 only (EN-4403): a real exception an operator must clear. */
  held?: boolean;
  /** V2 only (EN-4544): sent to the provider, or waiting for an operator's Send. */
  provider_send?: OrderProviderSend | null;
  items?: FulfillmentRecordItem[];
  is_deliverr?: boolean;
  is_fba?: boolean;
};

export type OrderIdCellSelection = {
  isSelected: boolean;
  /** False when the order cannot join the current batch. */
  canSelect: boolean;
  /** The operator's role does not allow acting on this list. */
  isDisabled?: boolean;
  onToggle: () => void;
};

export type OrderIdCellProps = {
  order: OrderIdCellOrder;
  selection?: OrderIdCellSelection;
  /** Replaces the checkbox for orders that cannot be selected. */
  notice?: string;
  isDimmed?: boolean;
  isShipped?: boolean;
  addressFix?: (event: MouseEvent) => void;
  /** Reasons worked out by the screen rather than recorded by the pipeline. */
  extraIssues?: OrderIssue[];
  /** Off where a column already prints the reason (the Held tab). */
  showStatusReason?: boolean;
  /** Off where the tab already says it (a canceled order on the Canceled tab). */
  showStage?: boolean;
};

function FulfillmentId({ label, value }: { label: string; value: string }) {
  return (
    <span className="sk:inline-flex sk:items-center sk:gap-1 sk:text-xs sk:text-fg-tertiary">
      <span className="sk:whitespace-nowrap">
        {label}: {withOrderPrefix(value)}
      </span>
      <CopyButton value={withOrderPrefix(value)} label={`Copy ${label}`} />
    </span>
  );
}

type CopyTarget = { label: string; value: string };

/** The row's one copy control: every code it shows, and the public link when there is one. */
function CopyMenu({ targets }: { targets: CopyTarget[] }) {
  return (
    <Popover
      aria-label="Copy"
      placement="bottom start"
      trigger={
        <Button
          variant="pill"
          shape="circle"
          aria-label="Copy"
          className="sk:size-6 sk:bg-fill-quaternary sk:shadow-none"
        >
          <CopySimpleIcon size={12} />
        </Button>
      }
    >
      {({ close }) => (
        <div className="sk:flex sk:w-44 sk:flex-col">
          {targets.map(target => (
            <CopyMenuItem
              key={target.label}
              value={target.value}
              label={target.label}
              onDone={close}
            />
          ))}
        </div>
      )}
    </Popover>
  );
}

function CopyMenuItem({
  value,
  label,
  onDone,
}: {
  value: string;
  label: string;
  onDone: () => void;
}) {
  return (
    <Button
      variant="menu"
      size="md"
      onPress={async () => {
        await copyToClipboard(value);
        onDone();
      }}
    >
      {label}
    </Button>
  );
}

export function OrderIdCell({
  order,
  selection,
  notice,
  isDimmed,
  isShipped,
  addressFix,
  extraIssues,
  showStatusReason = true,
  showStage = true,
}: OrderIdCellProps) {
  const displayId = order.marketplace_order_display_id || '';
  const publicLink = getPublicLabelLink(order.order_error);
  const copyTargets: CopyTarget[] = [
    { label: 'Copy order ID', value: displayId },
    ...(publicLink ? [{ label: 'Copy public link', value: publicLink }] : []),
  ];

  const deliverrId = order.is_deliverr
    ? getFulfillmentOrderId(order.items, false)
    : '';
  const fbaId = order.is_fba ? getFulfillmentOrderId(order.items, true) : '';
  const pastSla = getPastSla(order);
  const statusReason = showStatusReason
    ? getStatusReason(order, !!pastSla)
    : null;
  /* EN-4544: where the order is with its provider; it says more than the Routed or At provider stage, so that steps aside. */
  const providerSend = getProviderSend(order);
  const showsStage =
    showStage &&
    !(
      providerSend &&
      (order.stage === 'routed' || order.stage === 'at_provider')
    );
  const errors = withoutWaitingForSend(order.order_error, order);
  /* An issue chip already says why the order is held, so Held shows only when nothing else does. */
  const isHoldExplained =
    getOrderIssues(errors, isShipped).length + (extraIssues?.length ?? 0) > 0;

  return (
    <div className="sk:flex sk:items-start sk:gap-2.5">
      {selection ? (
        // biome-ignore lint/a11y/noStaticElementInteractions: the handler only stops a click, it does not add one
        // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard never reaches this span, the checkbox inside it takes the focus
        <span
          className="sk:flex sk:h-8 sk:w-4 sk:shrink-0 sk:items-center sk:justify-center"
          onClick={event => event.stopPropagation()}
        >
          <Tooltip
            content={tooltipTexts.missingPermission}
            placement="top start"
            isDisabled={!selection.isDisabled}
          >
            <Checkbox
              aria-label={`Select order ${displayId}`}
              isSelected={selection.isSelected}
              isDisabled={selection.isDisabled || !selection.canSelect}
              onChange={selection.onToggle}
            />
          </Tooltip>
        </span>
      ) : null}

      <div>
        {/* The chips stay beside the ID however narrow the column: they
            truncate (the full text is on hover) rather than drop under it. */}
        <div className="sk:flex sk:items-center sk:gap-2.5">
          <div className="sk:flex sk:min-w-44 sk:shrink-0 sk:items-center sk:gap-2.5">
            <MarketplaceAvatar
              marketplace={order.marketplace}
              fallbackText={order.marketplace?.type}
              shopName={order.shopName || channelLabel(order.marketplace?.type)}
              isDimmed={isDimmed}
              size="lg"
            />

            {/* The marketplace's number. Its line keeps a 20px box so its 24px
                buttons overhang it. The Skupreme # is on the order page and in
                the search, not here. */}
            <div className="sk:flex sk:min-w-0 sk:flex-col">
              <div className="sk:flex sk:h-5 sk:min-w-0 sk:items-center sk:gap-2.5">
                {/* Plain text: the row itself is the link, and links cannot nest. */}
                <span className="sk:min-w-0 sk:truncate sk:font-medium sk:text-fg">
                  {displayId}
                </span>

                {displayId ? (
                  <StopRowClick>
                    {copyTargets.length > 1 ? (
                      <CopyMenu targets={copyTargets} />
                    ) : (
                      <CopyButton value={displayId} label="Copy order ID" />
                    )}
                  </StopRowClick>
                ) : null}

                {notice ? (
                  <Tooltip content={notice} placement="top start">
                    <InfoIcon size={14} className="sk:text-fg-tertiary" />
                  </Tooltip>
                ) : null}

                {order.memo ? (
                  <StopRowClick>
                    <Tooltip
                      placement="bottom"
                      content={
                        <span className="sk:flex sk:flex-col sk:gap-1.5">
                          <span>Comment</span>
                          <span>{order.memo}</span>
                        </span>
                      }
                    >
                      <span
                        className={buttonStyles({
                          shape: 'circle',
                          className:
                            'sk:size-6 sk:cursor-default sk:bg-fill-quaternary sk:shadow-none',
                        })}
                      >
                        <ChatIcon size={12} />
                      </span>
                    </Tooltip>
                  </StopRowClick>
                ) : null}
              </div>
            </div>
          </div>

          {/* One column of chips beside the ID, the issues' width; empty, it takes no gap. */}
          <div className="sk:flex sk:max-w-50 sk:min-w-0 sk:flex-col sk:items-start sk:gap-1.5 sk:empty:hidden">
            <OrderIssues
              layout="aside"
              errors={errors}
              isShipped={isShipped}
              addressFix={addressFix}
              extra={extraIssues}
            />

            {order.partially_canceled ? (
              <StopRowClick>
                <PartiallyCanceledBadge
                  cancellation={order.partially_canceled}
                  compact
                />
              </StopRowClick>
            ) : null}

            {pastSla ? (
              <StopRowClick>
                <PastSlaBadge pastSla={pastSla} compact />
              </StopRowClick>
            ) : null}

            {order.held && !isHoldExplained ? (
              <StopRowClick>
                <HeldBadge held compact />
              </StopRowClick>
            ) : null}

            {showsStage ? <StageBadge order={order} compact /> : null}

            {providerSend ? (
              <StopRowClick>
                <ProviderSendBadge order={order} compact />
              </StopRowClick>
            ) : null}

            {statusReason ? (
              <StopRowClick>
                <StatusReasonBadge reason={statusReason} compact />
              </StopRowClick>
            ) : null}
          </div>
        </div>

        {deliverrId || fbaId ? (
          <div className="sk:mt-1 sk:flex sk:flex-wrap sk:items-center sk:gap-x-4">
            {deliverrId ? (
              <FulfillmentId label="Deliverr ID" value={deliverrId} />
            ) : null}
            {fbaId ? <FulfillmentId label="FBA ID" value={fbaId} /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
